/**
 * ProfileLinkManagementModal — Manage members of a Player Profile shell.
 *
 * Lists current member roster entries (with team chip + link code), lets
 * the user disconnect any member, and add new members either via:
 *   - "Add from Favorites" (multi-select, like AddProfileModal)
 *   - "Paste a Link Code"
 *
 * Triggered from the 🔗 icon in the profile header. The owning page
 * passes the current shell + a refresh callback.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const NAVY = '#0B1824';
const ORANGE = '#e8963a';
const MUTED = '#8FA3B1';
const BORDER = '#DDE5EC';

export default function ProfileLinkManagementModal({
  open, onOpenChange, profile, onChange,
}) {
  const [favorites, setFavorites] = useState([]);
  const [favLoading, setFavLoading] = useState(false);
  const [code, setCode] = useState('');
  const [adding, setAdding] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCode('');
    setUnlinkTarget(null);
    setFavLoading(true);
    api.get('/players/favorites')
      .then(r => setFavorites(r.data?.players || []))
      .catch(() => setFavorites([]))
      .finally(() => setFavLoading(false));
  }, [open]);

  const memberIds = new Set((profile?.members || []).map(m => m.player_id));
  const addableFavorites = favorites.filter(f => !memberIds.has(f.player_id));

  const addMember = async (payload, label) => {
    if (!profile) return;
    setAdding(true);
    try {
      const r = await api.post(`/profiles/${profile.profile_id}/members`, payload);
      toast.success(`${label} added`);
      onChange && onChange(r.data);
      setCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add');
    } finally {
      setAdding(false);
    }
  };

  const submitCode = async () => {
    const c = code.trim().toUpperCase();
    if (!/^[A-Z0-9]{8}$/.test(c)) {
      toast.error('Code must be 8 characters (A–Z, 0–9).');
      return;
    }
    await addMember({ code: c }, `Player`);
  };

  const confirmUnlink = async () => {
    if (!unlinkTarget || !profile) return;
    try {
      const r = await api.delete(`/profiles/${profile.profile_id}/members/${unlinkTarget.player_id}`);
      toast.success('Disconnected');
      setUnlinkTarget(null);
      onChange && onChange(r.data);
    } catch {
      toast.error('Could not disconnect');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="profile-link-modal">
          <DialogHeader>
            <DialogTitle
              className="text-lg font-bold uppercase tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Linked Teams
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              Manage which roster entries feed stats into{' '}
              <strong>{profile?.name || 'this profile'}</strong>. Disconnecting a
              link removes that team&apos;s data from the consolidated stats.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-2" data-testid="profile-members-list">
            {(profile?.members || []).length === 0 ? (
              <p className="text-[13px] py-3 text-center" style={{ color: MUTED }}>
                No teams linked yet.
              </p>
            ) : (
              (profile?.members || []).map(m => (
                <button
                  key={m.player_id}
                  type="button"
                  onClick={() => setUnlinkTarget(m)}
                  data-testid={`profile-member-${m.player_id}`}
                  className="w-full rounded-xl p-3 flex items-center gap-3 transition-all hover:bg-slate-50 active:scale-[.99]"
                  style={{ background: '#fff', border: `1.5px solid ${BORDER}` }}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 16 }}>
                    {m.jersey_number || '–'}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-[15px] font-bold truncate leading-tight" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {m.name}
                    </p>
                    <p className="text-[11px] mt-0.5 truncate uppercase tracking-[.08em]" style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {m.team_name || '—'} · {m.link_code}
                    </p>
                  </div>
                  <X className="w-4 h-4 shrink-0" style={{ color: MUTED }} />
                </button>
              ))
            )}
          </div>

          {/* Add from Favorites */}
          <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${BORDER}` }}>
            <Label className="text-[11px] font-bold uppercase tracking-[.12em] mb-2 block"
              style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
              Add from Favorites
            </Label>
            {favLoading ? (
              <p className="text-[12px]" style={{ color: MUTED }}>Loading…</p>
            ) : addableFavorites.length === 0 ? (
              <p className="text-[12px]" style={{ color: MUTED }}>
                {favorites.length === 0
                  ? 'No favorited players yet.'
                  : 'All your favorites are already linked.'}
              </p>
            ) : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {addableFavorites.map(p => (
                  <button
                    key={p.player_id}
                    type="button"
                    disabled={adding}
                    onClick={() => addMember({ player_id: p.player_id }, p.name)}
                    data-testid={`profile-add-fav-${p.player_id}`}
                    className="w-full rounded-xl px-3 py-2 flex items-center gap-3 transition-all hover:bg-slate-50 active:scale-[.99]"
                    style={{ background: '#fff', border: `1.5px solid ${BORDER}` }}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 13 }}>
                      {p.jersey_number || '–'}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-[13px] font-bold truncate leading-tight" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {p.name}
                      </p>
                      {p.team_name && (
                        <p className="text-[10px] mt-0.5 truncate uppercase tracking-[.06em]" style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
                          {p.team_name}
                        </p>
                      )}
                    </div>
                    <Plus className="w-4 h-4 shrink-0" style={{ color: NAVY }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Add via code */}
          <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${BORDER}` }}>
            <Label className="text-[11px] font-bold uppercase tracking-[.12em] mb-2 block"
              style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
              Paste a Link Code
            </Label>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                placeholder="ABCD1234"
                maxLength={8}
                data-testid="profile-link-code-input"
                className="tracking-[0.32em] uppercase font-mono text-center text-base"
              />
              <Button
                type="button"
                onClick={submitCode}
                disabled={adding || code.length !== 8}
                data-testid="profile-link-code-submit"
                className="h-10 px-4 rounded-full font-bold uppercase"
                style={{ background: NAVY, color: '#fff' }}
              >
                <Check className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disconnect confirmation */}
      <Dialog open={!!unlinkTarget} onOpenChange={(o) => { if (!o) setUnlinkTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="profile-unlink-modal">
          <DialogHeader>
            <DialogTitle
              className="text-lg font-bold uppercase tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Disconnect Team?
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              {unlinkTarget?.name}{unlinkTarget?.team_name ? ` (${unlinkTarget.team_name})` : ''} will no longer feed stats into this profile. You can re-add anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setUnlinkTarget(null)}
              className="flex-1 h-11 rounded-full"
              data-testid="profile-unlink-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmUnlink}
              data-testid="profile-unlink-confirm"
              className="flex-1 h-11 rounded-full font-bold uppercase"
              style={{ background: '#dc2626', color: '#fff' }}
            >
              Disconnect
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
